import React, { useEffect, useState } from "react";
import {
  Building2,
  User,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Loader2,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { transfersApi, ProjectTransfer } from "@/services/api/transfers";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import * as errorUtils from '@/utils/errors';

/**
 * Transfer Management Panel
 *
 * Displays pending transfer requests for organizations where the user is an admin/owner.
 * Allows approving or rejecting transfer requests.
 */
export const TransferManagementPanel: React.FC = () => {
  const [transfers, setTransfers] = useState<ProjectTransfer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTransfer, setSelectedTransfer] = useState<ProjectTransfer | null>(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchTransfers = async () => {
    setIsLoading(true);
    try {
      const data = await transfersApi.getPendingTransfers();
      setTransfers(data);
    } catch (error) {
      console.error("Failed to fetch pending transfers:", error);
      toast.error("Failed to load transfer requests");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTransfers();
  }, []);

  const handleApprove = async (transfer: ProjectTransfer) => {
    setIsSubmitting(true);
    try {
      await transfersApi.approveTransfer(transfer._id);
      toast.success(
        `Transfer approved. "${transfer.projectId.name}" is now owned by ${transfer.toOwnerId.name}`
      );
      // Refresh the list
      fetchTransfers();
    } catch (error: unknown) {
      console.error("Failed to approve transfer:", error);
      toast.error(
        errorUtils.getApiErrorMessage(error, "Failed to approve transfer request")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRejectClick = (transfer: ProjectTransfer) => {
    setSelectedTransfer(transfer);
    setRejectionReason("");
    setRejectDialogOpen(true);
  };

  const handleRejectSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedTransfer) return;

    if (rejectionReason.trim().length < 10) {
      toast.error("Rejection reason must be at least 10 characters");
      return;
    }

    setIsSubmitting(true);
    try {
      await transfersApi.rejectTransfer(selectedTransfer._id, {
        reason: rejectionReason.trim(),
      });
      toast.success("Transfer request rejected");
      setRejectDialogOpen(false);
      setSelectedTransfer(null);
      // Refresh the list
      fetchTransfers();
    } catch (error: unknown) {
      console.error("Failed to reject transfer:", error);
      toast.error(
        errorUtils.getApiErrorMessage(error, "Failed to reject transfer request")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader>
          <CardTitle>Pending Transfer Requests</CardTitle>
          <CardDescription>
            Review and approve project transfers to your organizations
          </CardDescription>
        </CardHeader>
        <CardContent className="flex-1">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (transfers.length === 0) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader>
          <CardTitle>Pending Transfer Requests</CardTitle>
          <CardDescription>
            Review and approve project transfers to your organizations
          </CardDescription>
        </CardHeader>
        <CardContent className="flex-1">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-sm text-muted-foreground">
              No pending transfer requests
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="h-full flex flex-col">
        <CardHeader>
          <CardTitle>
            Pending Transfer Requests
            <Badge variant="secondary" className="ml-2">
              {transfers.length}
            </Badge>
          </CardTitle>
          <CardDescription>
            Review and approve project transfers to your organizations
          </CardDescription>
        </CardHeader>
        <CardContent className="flex-1">
          <div className="space-y-4 max-h-[280px] overflow-y-auto">
            {transfers.map((transfer) => {
              const expiresIn = formatDistanceToNow(new Date(transfer.expiresAt), {
                addSuffix: true,
              });

              return (
                <div
                  key={transfer._id}
                  className="rounded-lg border border-border bg-card p-4 space-y-4"
                >
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <h3 className="font-semibold text-lg">
                        {transfer.projectId.name}
                      </h3>
                      {transfer.projectId.description && (
                        <p className="text-sm text-muted-foreground">
                          {transfer.projectId.description}
                        </p>
                      )}
                    </div>
                    <Badge variant="outline" className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Expires {expiresIn}
                    </Badge>
                  </div>

                  {/* Transfer Flow */}
                  <div className="flex items-center gap-3 text-sm">
                    <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-muted">
                      <User className="h-4 w-4" />
                      <span className="font-medium">
                        {transfer.requestedBy.username}
                      </span>
                    </div>
                    <div className="text-muted-foreground">→</div>
                    <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-muted">
                      <Building2 className="h-4 w-4" />
                      <span className="font-medium">
                        {transfer.toOwnerId.name}
                      </span>
                    </div>
                  </div>

                  {/* Reason */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Transfer Reason</Label>
                    <p className="text-sm text-muted-foreground bg-muted p-3 rounded-md">
                      {transfer.reason}
                    </p>
                  </div>

                  {/* Notes (if provided) */}
                  {transfer.notes && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Additional Notes</Label>
                      <p className="text-sm text-muted-foreground bg-muted p-3 rounded-md">
                        {transfer.notes}
                      </p>
                    </div>
                  )}

                  {/* Resource Info */}
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <div>
                      Components: {transfer.dependentResources.componentCount}
                    </div>
                    <div>
                      Requested:{" "}
                      {formatDistanceToNow(new Date(transfer.requestedAt), {
                        addSuffix: true,
                      })}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-2">
                    <Button
                      onClick={() => handleApprove(transfer)}
                      disabled={isSubmitting}
                      className="flex-1"
                    >
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Approve Transfer
                    </Button>
                    <Button
                      onClick={() => handleRejectClick(transfer)}
                      variant="destructive"
                      disabled={isSubmitting}
                      className="flex-1"
                    >
                      <XCircle className="mr-2 h-4 w-4" />
                      Reject
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Reject Transfer Request</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this transfer request. This will be
              shared with the requester.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleRejectSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="rejectionReason">
                Rejection Reason <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="rejectionReason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Explain why you're rejecting this transfer (10-500 characters)"
                rows={5}
                maxLength={500}
                required
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Minimum 10 characters</span>
                <span>{rejectionReason.length}/500</span>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setRejectDialogOpen(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="destructive"
                disabled={
                  isSubmitting ||
                  rejectionReason.trim().length < 10 ||
                  rejectionReason.trim().length > 500
                }
              >
                {isSubmitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Reject Transfer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
